import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, DocumentArrowDownIcon, EyeIcon, PencilSquareIcon, TruckIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { openFile } from "../../utils/openFile";
import { Button, Container, EmptyState, Loading, Modal, PageHeader, RowAction, Table, Tag, Textarea } from "../ui";
import { abroadDecisionsApi } from "../../api/abroadLoans";
import { errorMessageOf, problemOf } from "../../api/scholarships";
import { formatDateTime, formatScore } from "../scholarships/review/model";
import { loanSummary } from "../scholarships/funding";
import ApprovedFundingDialog from "../scholarships/review/ApprovedFundingDialog";
import { HOLDER, LOAN_STATUSES, MY_QUEUE, actionsFor, loanStatusOf, myLoanRole, sendsToMinister } from "../scholarships/loans/model";
import { ABROAD_KEY, callPhaseOf, useAbroadCall } from "./model";
import { approvalsPath, callApprovalsPath, useDecisions } from "./approvals";
import { fundingEntryOf } from "./review";
import AwardProgressDialog from "./AwardProgressDialog";
import ScoredApplicationDialog from "./ScoredApplicationDialog";

const MAX_TEXT = 1000;
const countsOf = (rows) => rows.reduce((acc, r) => ({ ...acc, [r.state]: (acc[r.state] ?? 0) + 1 }), {});
const statusTag = (state) => <Tag color={loanStatusOf(state).color}>{loanStatusOf(state).label}</Tag>;
const studentsLabel = (rows) => (rows.length === 1 ? rows[0].applicantName : `${rows.length} students`);

// The calls whose award lists went to the DG, with how many awards wait for the signed-in decider
export function AbroadApprovalsPage() {
  const navigate = useNavigate();
  const role = myLoanRole();
  const { data: rows = [], isPending, isError, refetch } = useDecisions();
  const open = (call) => navigate(callApprovalsPath(call.callId));

  const calls = Object.values(
    rows.reduce((acc, r) => {
      const call = acc[r.callId] ?? { callId: r.callId, callName: r.callName, rows: [] };
      call.rows.push(r);
      return { ...acc, [r.callId]: call };
    }, {})
  ).map((c) => ({ ...c, counts: countsOf(c.rows) }));
  const waiting = (c) => (MY_QUEUE[role] ?? []).reduce((sum, state) => sum + (c.counts[state] ?? 0), 0);
  const inProgress = (c) => (c.counts.WITH_DG ?? 0) + (c.counts.WITH_MINISTER ?? 0) + (c.counts.RETURNED_TO_HEC ?? 0);

  const columns = [
    { key: "callName", label: "Loan call", render: (c) => <span className="font-medium text-slate-800">{c.callName}</span> },
    { key: "waiting", label: "Waiting for you", accessor: waiting, render: (c) => (waiting(c) ? <Tag color="blue">{waiting(c)}</Tag> : "—") },
    { key: "inProgress", label: "In progress", accessor: inProgress },
    { key: "approved", label: "Approved", accessor: (c) => c.counts.APPROVED ?? 0 },
    { key: "rejected", label: "Rejected", accessor: (c) => c.counts.REJECTED ?? 0 },
    { key: "reserve", label: "Reserve", accessor: (c) => c.counts.RESERVE ?? 0 },
    { key: "open", label: "", exportable: false, render: (c) => <RowAction Icon={EyeIcon} label="Approvals" onClick={() => open(c)} /> },
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title="Abroad loans: approvals" subtitle="Abroad loans HEC sent to the DG, then the Minister, for approval." />
      {isError ? (
        <EmptyState title="Could not load the approvals" onRefresh={refetch} />
      ) : (
        <Table cardView columns={columns} data={calls} onRowClick={open} isLoading={isPending} searchable={false} exportName="abroad-loan-approvals" emptyTitle="No loans sent to the DG yet" />
      )}
    </Container>
  );
}

// One decision on the chosen awards: a rejection or a return needs a reason; what the DG sends to the Minister may carry a note
function DecisionDialog({ decision, chosen, role, onClose, onDone }) {
  const [text, setText] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const withNote = sendsToMinister(role, decision.action);

  const submit = async () => {
    setError(null);
    if (decision.reason && !text.trim()) return setError("Say why.");
    setBusy(true);
    try {
      await abroadDecisionsApi.decide(
        chosen.map((r) => r.applicationId),
        decision.action,
        text.trim() ? { [withNote ? "note" : "reason"]: text.trim() } : {}
      );
      toast.success(`${decision.label}: ${studentsLabel(chosen)}`);
      onDone();
    } catch (err) {
      console.error(`Failed to ${decision.action} abroad loans ${chosen.map((r) => r.applicationId).join(", ")}:`, err);
      // `invalid-decision` means something else on bursaries, so its message is set here
      setError(problemOf(err)?.code === "invalid-decision" ? "Some of the selected students are no longer with you. Reload and try again." : errorMessageOf(err, "Failed to save the decision. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      dismissible={!busy}
      title={`${decision.label}: ${studentsLabel(chosen)}`}
      description={chosen.length > 1 ? chosen.map((r) => r.applicantName).join(", ") : null}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" variant={decision.destructive ? "danger" : "solid"} onClick={submit} loading={busy}>
            {decision.label}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {(decision.reason || withNote) && (
          <Textarea
            label={decision.reason ? "Reason (the student sees it)" : "Note for the Minister (optional)"}
            required={decision.reason}
            rows={3}
            maxLength={MAX_TEXT}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}

/**
 * One call's awards and reserves on their way through the DG and the Minister, best HEC score
 * first. The signed-in role decides which can be selected, which decisions show and whose amounts
 * can change; an approved loan's letter can be downloaded.
 */
export function AbroadCallApprovalsPage() {
  const { callId } = useParams();
  const queryClient = useQueryClient();
  const role = myLoanRole();
  const { data: call, isPending: callPending, isError: callError, refetch: refetchCall } = useAbroadCall(callId);
  const { data: rows = [], isPending, isError, refetch } = useDecisions(callId);
  const [selected, setSelected] = useState([]);
  const [deciding, setDeciding] = useState(null); // { decision, chosen }
  const [amountsFor, setAmountsFor] = useState(null);
  const [savingAmounts, setSavingAmounts] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [progressOf, setProgressOf] = useState(null);

  if (callPending) return <Loading label="Loading the loan call…" />;
  if (callError) return <EmptyState title="Could not load this loan call" onRefresh={refetchCall} />;

  const mine = rows.filter((r) => actionsFor(role, r.state).length);
  const chosen = rows.filter((r) => selected.includes(r.applicationId));
  // The decisions every chosen award allows
  const common = chosen.length ? actionsFor(role, chosen[0].state).filter((d) => chosen.every((r) => actionsFor(role, r.state).some((x) => x.action === d.action))) : [];
  const allSelected = mine.length > 0 && mine.every((r) => selected.includes(r.applicationId));
  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const counts = countsOf(rows);

  // Decisions move applications on every abroad screen: the award list, the scores and the student's
  const refresh = () => queryClient.invalidateQueries({ queryKey: ABROAD_KEY });

  const decided = () => {
    refresh();
    setSelected([]);
    setDeciding(null);
  };

  const saveAmounts = async (amounts, reason) => {
    setSavingAmounts(true);
    try {
      await abroadDecisionsApi.changeAmounts(amountsFor.applicationId, amounts, reason);
      toast.success(`Loan amounts saved for ${amountsFor.applicantName}`);
      refresh();
      setAmountsFor(null);
    } catch (err) {
      console.error(`Failed to change the approved amounts of abroad application ${amountsFor.applicationId}:`, err);
      toast.error(errorMessageOf(err, "Failed to save the amounts. Please try again."));
    } finally {
      setSavingAmounts(false);
    }
  };

  const downloadLetter = (r) => openFile(async () => (await abroadDecisionsApi.letter(r.applicationId)).data, `approval-letter-${r.letterReference.replaceAll("/", "-")}.pdf`);

  const checkbox = (props) => <input type="checkbox" className="h-4 w-4 accent-brand" {...props} />;
  const columns = [
    ...(mine.length
      ? [
          {
            key: "select",
            label: checkbox({ "aria-label": "Select every student you can decide on", checked: allSelected, onChange: () => setSelected(allSelected ? [] : mine.map((r) => r.applicationId)) }),
            exportable: false,
            sortable: false,
            render: (r) =>
              actionsFor(role, r.state).length
                ? checkbox({ "aria-label": `Select ${r.applicantName}`, checked: selected.includes(r.applicationId), onClick: (e) => e.stopPropagation(), onChange: () => toggle(r.applicationId) })
                : null,
          },
        ]
      : []),
    { key: "applicant", label: "Student", accessor: (r) => r.applicantName, render: (r) => <span className="font-medium text-slate-800">{r.applicantName}</span> },
    { key: "programme", label: "Programme", accessor: (r) => r.programmeName, filter: "Any programme" },
    { key: "averageScore", label: "HEC score", accessor: (r) => formatScore(r.averageScore), sortValue: (r) => r.averageScore },
    { key: "requested", label: "Requested", accessor: (r) => loanSummary(r.requested) },
    {
      key: "approved",
      label: "Approved",
      accessor: (r) => (r.approvedAmounts ? loanSummary(r.approvedAmounts) : "—"),
      render: (r) => (
        <span className="flex items-center gap-1">
          {r.approvedAmounts ? loanSummary(r.approvedAmounts) : "—"}
          {HOLDER[r.state]?.includes(role) && <RowAction Icon={PencilSquareIcon} label="Change amounts" onClick={() => setAmountsFor(r)} />}
        </span>
      ),
    },
    { key: "state", label: "Status", accessor: (r) => loanStatusOf(r.state).label, filter: "Any status", render: (r) => statusTag(r.state) },
    { key: "submittedAt", label: "Submitted", accessor: (r) => formatDateTime(r.submittedAt), sortValue: (r) => r.submittedAt },
    {
      key: "actions",
      label: "",
      exportable: false,
      render: (r) => (
        <span className="flex items-center gap-1">
          {r.letterReference && <RowAction Icon={DocumentArrowDownIcon} label="Approval letter" onClick={() => downloadLetter(r)} />}
          {r.state === "APPROVED" && <RowAction Icon={TruckIcon} label="Loan progress" onClick={() => setProgressOf(r)} />}
          <RowAction Icon={EyeIcon} label="View" onClick={() => setOpenRow(r)} />
        </span>
      ),
    },
  ];

  const phase = callPhaseOf(call);
  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to={approvalsPath} className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> All loan approvals
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {call.name}
            <Tag color={phase.color}>{phase.label}</Tag>
          </span>
        }
        subtitle={call.academicYearName}
      />
      <div className="flex flex-wrap gap-2">
        {LOAN_STATUSES.map((s) => (
          <Tag key={s.value} color={s.color}>
            {s.label}: {counts[s.value] ?? 0}
          </Tag>
        ))}
      </div>
      {isError ? (
        <EmptyState title="Could not load the approvals" onRefresh={refetch} />
      ) : (
        <Table cardView viewKey="abroad-call-approvals"
          columns={columns}
          data={rows}
          onRowClick={setOpenRow}
          isLoading={isPending}
          exportName={`approvals-${call.name}`}
          emptyTitle="No awards sent for approval"
          actions={
            common.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {common.map((d) => (
                  <Button key={d.action} size="sm" variant={d.destructive ? "danger-soft" : d.reason ? "outline" : "solid"} onClick={() => setDeciding({ decision: d, chosen })}>
                    {d.label} ({chosen.length})
                  </Button>
                ))}
              </div>
            )
          }
        />
      )}
      {deciding && <DecisionDialog role={role} {...deciding} onClose={() => setDeciding(null)} onDone={decided} />}
      <ApprovedFundingDialog
        key={amountsFor?.applicationId}
        entry={fundingEntryOf(amountsFor)}
        withReason
        busy={savingAmounts}
        onApply={saveAmounts}
        onClose={() => setAmountsFor(null)}
      />
      <ScoredApplicationDialog key={openRow?.applicationId} call={call} row={openRow} onClose={() => setOpenRow(null)} />
      {progressOf && <AwardProgressDialog applicationId={progressOf.applicationId} studentName={progressOf.applicantName} onClose={() => setProgressOf(null)} />}
    </Container>
  );
}
